import React, { useCallback, useContext, useState } from 'react';
import BaseMappingTable from './BaseMappingTable';
import { MappingContext, MappingContextType } from '../Contexts/MappingContext';
import { validateAttributeValueToggle } from '../Utils/attributeValidation';
import { getMappingDropdownOptions } from '../Services/MappingServices';
import { mergeMappedOptions } from '../Utils/mergeMappedOptions';
import CreateAttributeValueModal from '../../CatalogManagement/modals/CreateAttributeValueModal';

const AttributeValueMappingTable = () => {
  const {
    channelKey,
    updateAttributeValue,
    attributeValueRowActions,
    setAttributeValueRowActions,
    selectedAttribute,
  } = useContext<MappingContextType>(MappingContext);

  const [createForRow, setCreateForRow] = useState<MappingAttributeValue | null>(null);

  // Values created in this session under the currently-selected attribute,
  // keyed by attribute name so switching attributes doesn't leak options.
  const [locallyCreated, setLocallyCreated] = useState<Record<string, string[]>>({});

  // Phase 2: AV docs are now keyed on the *mapped* attribute. The top-of-page
  // selector hands us the mapped attribute directly via selectedAttribute —
  // its `attribute` field IS the mapped attribute name (returned by the
  // attributevalue-attributes endpoint that groups AV docs by mapped_attribute).
  // We still fall back to mapped_attributes[0] / mapped_attribute (legacy)
  // for back-compat in case the selector hands an attribute_mapping doc.
  const mappedParentAttribute =
    selectedAttribute?.attribute
    || selectedAttribute?.mapped_attributes?.[0]
    || selectedAttribute?.mapped_attribute
    || '';

  const loadMappedOptions = useCallback(
    async (search: string, page: number) => {
      if (!channelKey || !mappedParentAttribute) {
        return { options: [], hasMore: false };
      }
      const res = await getMappingDropdownOptions(
        channelKey,
        mappedParentAttribute,
        page,
        40,
        search,
      );
      const rawList: { attribute_value?: string; mapped_attribute_value?: string }[] =
        res?.data?.attribute_values || [];
      const list: string[] = rawList
        .map((v) => v?.mapped_attribute_value || v?.attribute_value || '')
        .filter(Boolean);
      const localForAttr = locallyCreated[mappedParentAttribute] || [];
      const merged = mergeMappedOptions(localForAttr, list, search);

      return {
        options: merged.map((name) => ({ value: name, label: name })),
        hasMore: res?.data?.has_more ?? false,
      };
    },
    [channelKey, mappedParentAttribute, locallyCreated],
  );

  return (
    <>
      <BaseMappingTable<MappingAttributeValue>
        fields={{
          incoming: 'attribute_value',
          // Phase 2: BE field name `mapped_attribute_value` (singular) is
          // preserved from the legacy schema; type changed from string to
          // string[].
          mapped: 'mapped_attribute_value',
          considered: 'isConsidered',
          reviewed: 'isReviewed',
        }}
        labels={{
          incoming: 'Incoming Attribute Value',
          mapped: 'Mapped Attribute Values',
        }}
        onUpdate={updateAttributeValue}
        rowActions={attributeValueRowActions}
        setRowActions={setAttributeValueRowActions}
        validateToggle={(item, allCategories) => {
          const attributeName = mappedParentAttribute;
          const mappedList = Array.isArray(item.mapped_attribute_value)
            ? item.mapped_attribute_value
            : (item.mapped_attribute_value ? [item.mapped_attribute_value as unknown as string] : []);
          const attributeValue = item.attribute_value || mappedList[0] || '';
          return validateAttributeValueToggle(attributeName, attributeValue, allCategories);
        }}
        loadMappedOptions={loadMappedOptions}
        onCreateMapped={(item) => setCreateForRow(item)}
        testIdPrefix="attribute-value"
      />

      {createForRow && mappedParentAttribute && (
        <CreateAttributeValueModal
          isOpen
          onClose={() => setCreateForRow(null)}
          onAdd={(values) => {
            // Phase 2: append the new value(s) to this row's
            // `mapped_attribute_value` array (list-typed, BE field name kept
            // singular for back-compat with existing schema), preserving
            // existing selections.
            const rawExisting = createForRow.mapped_attribute_value;
            const existing = Array.isArray(rawExisting)
              ? rawExisting
              : (rawExisting ? [rawExisting as unknown as string] : []);
            const merged = Array.from(new Set([...existing, ...values]));
            updateAttributeValue(createForRow.attribute_value, {
              mapped_attribute_value: merged,
            });
            setLocallyCreated((prev) => {
              const existingLocal = new Set(prev[mappedParentAttribute] || []);
              values.forEach((v) => existingLocal.add(v));
              return { ...prev, [mappedParentAttribute]: Array.from(existingLocal) };
            });
            setCreateForRow(null);
          }}
          // Phase 2: mappedParentAttribute IS the mapped attribute name (the
          // AV page is grouped by mapped_attribute), so it's the correct
          // parent to attach new value nodes to in Neo4j.
          attributeName={mappedParentAttribute}
          csKey={channelKey}
          showInfoBar={false}
        />
      )}
    </>
  );
};

export default AttributeValueMappingTable;
